import { ArrowRightIcon } from "@heroicons/react/outline";

import { useEvent } from "../../../changelog-item-context";
import EntityLink from "../../EntityLink";
import { getVersionEntityLink } from "../../utils";
import EventTitleWrapper, { ActionText } from "../EventTitleWrapper";

import LabelBadge from "@/components/ui/LabelBadge";
import type { DeployLabelActionPayload } from "@/queries/folder-entities/types";
import { EntityType } from "@/queries/folder-entities/types";

const detectEntityType = (payload: DeployLabelActionPayload): EntityType => {
  if ("prompt_template_id" in payload || "prompt_template_name" in payload) {
    return EntityType.PROMPT;
  }
  if ("tool_name" in payload || "tool_label_id" in payload) {
    return EntityType.TOOL;
  }
  return EntityType.WORKFLOW;
};

const getEntityName = (entityType: EntityType, payload: DeployLabelActionPayload): string => {
  switch (entityType) {
    case EntityType.PROMPT:
      return (payload as { prompt_template_name?: string }).prompt_template_name ?? "";
    case EntityType.TOOL:
      return (payload as { tool_name?: string }).tool_name ?? "";
    default:
      return (payload as { workflow_name?: string }).workflow_name ?? "";
  }
};

const getMoveLabelVersions = (
  entityType: EntityType,
  payload: DeployLabelActionPayload,
): { fromVersion: number | undefined; toVersion: number | undefined } => {
  switch (entityType) {
    case EntityType.PROMPT:
      return {
        fromVersion: (payload as { old_prompt_template_version_number?: number })
          .old_prompt_template_version_number,
        toVersion: (payload as { new_prompt_template_version_number?: number })
          .new_prompt_template_version_number,
      };
    case EntityType.TOOL:
      return {
        fromVersion: (payload as { old_tool_version_number?: number }).old_tool_version_number,
        toVersion: (payload as { new_tool_version_number?: number }).new_tool_version_number,
      };
    default:
      return {
        fromVersion: (payload as { old_workflow_version_number?: number })
          .old_workflow_version_number,
        toVersion: (payload as { new_workflow_version_number?: number })
          .new_workflow_version_number,
      };
  }
};

const getDeleteLabelVersion = (
  entityType: EntityType,
  payload: DeployLabelActionPayload,
): number | undefined => {
  switch (entityType) {
    case EntityType.PROMPT:
      return (payload as { prompt_template_version_number?: number })
        .prompt_template_version_number;
    case EntityType.TOOL:
      return (payload as { tool_version_number?: number }).tool_version_number;
    default:
      return (payload as { workflow_version_number?: number }).workflow_version_number;
  }
};

const DeployLabelEventTitle = () => {
  const event = useEvent();
  const payload = event.action_payload as DeployLabelActionPayload | null;

  if (!payload) {
    return null;
  }

  const entityType = detectEntityType(payload);
  const entityName = getEntityName(entityType, payload);
  const labelName = payload.label_name ?? "";
  const isMove = payload.original_action_type === "MOVE_LABEL";

  if (isMove) {
    const { fromVersion, toVersion } = getMoveLabelVersions(entityType, payload);

    if (!fromVersion || !toVersion || !event.entity_id) {
      return null;
    }

    const fromLink = getVersionEntityLink(
      event.workspace_id,
      entityType,
      event.entity_id,
      fromVersion,
    );
    const toLink = getVersionEntityLink(event.workspace_id, entityType, event.entity_id, toVersion);

    return (
      <EventTitleWrapper>
        <ActionText>Deployed move label</ActionText>
        {labelName && <LabelBadge label={labelName} variant="environment" />}
        <ActionText>from</ActionText>
        <EntityLink
          type={entityType}
          name={entityName}
          version={fromVersion}
          link={fromLink}
          className="opacity-60 hover:opacity-100"
        />
        <ArrowRightIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
        <EntityLink type={entityType} name={entityName} version={toVersion} link={toLink} />
      </EventTitleWrapper>
    );
  }

  const version = getDeleteLabelVersion(entityType, payload);

  if (!version || !event.entity_id) {
    return null;
  }

  const link = getVersionEntityLink(event.workspace_id, entityType, event.entity_id, version);

  return (
    <EventTitleWrapper>
      <ActionText>Deployed delete label</ActionText>
      {labelName && <LabelBadge label={labelName} variant="environment" />}
      <ActionText>from</ActionText>
      <EntityLink type={entityType} name={entityName} version={version} link={link} />
    </EventTitleWrapper>
  );
};

export default DeployLabelEventTitle;
